import React from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Calculator } from 'lucide-react';
const FormulaBlock = ({
  formula
}: {
  formula: string;
}) => <div className="bg-slate-900 rounded-lg p-4 my-4 flex items-center gap-3 overflow-x-auto" data-id="element-166">
    <Calculator className="w-5 h-5 text-indigo-400 flex-shrink-0" data-id="element-167" />
    <code className="font-mono text-sm text-indigo-100 whitespace-nowrap" data-id="element-168">
      {formula}
    </code>
  </div>;
export function Layer2FeatureEngineering() {
  return <SectionWrapper title="Layer 2: Feature Engineering" subtitle="Raw PSP data is transformed into derived features that power the scorecard. These features are the inputs to the model — not the raw figures themselves." data-id="element-169">
      <div className="space-y-8" data-id="element-170">
        {/* Weighted Average GMV */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8" data-id="element-171">
          <h3 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-172">
            Weighted Average GMV
          </h3>
          <p className="text-slate-600 mb-6" data-id="element-173">
            Recent months are given higher weight to bias toward current
            performance rather than stale history.
          </p>

          <div className="overflow-x-auto mb-6" data-id="element-174">
            <table className="w-full text-sm text-left" data-id="element-175">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-176">
                <tr data-id="element-177">
                  <th className="px-4 py-3 rounded-tl-lg" data-id="element-178">Period</th>
                  <th className="px-4 py-3" data-id="element-179">Weight</th>
                  <th className="px-4 py-3 rounded-tr-lg" data-id="element-180">Rationale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-181">
                <tr data-id="element-182">
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-183">
                    Last month (M-1)
                  </td>
                  <td className="px-4 py-3 text-indigo-600 font-semibold" data-id="element-184">
                    50%
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-185">
                    Most predictive of current state
                  </td>
                </tr>
                <tr data-id="element-186">
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-187">
                    Month M-2
                  </td>
                  <td className="px-4 py-3 text-indigo-600 font-semibold" data-id="element-188">
                    30%
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-189">Recent context</td>
                </tr>
                <tr data-id="element-190">
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-191">
                    Months M-3 to M-6 avg
                  </td>
                  <td className="px-4 py-3 text-indigo-600 font-semibold" data-id="element-192">
                    20%
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-193">
                    Baseline reference
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <FormulaBlock formula="GMV_weighted = (GMV_m1 × 0.50) + (GMV_m2 × 0.30) + (GMV_m3_to_m6_avg × 0.20)" data-id="element-194" />
        </div>

        {/* Revenue Volatility */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8" data-id="element-195">
          <h3 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-196">
            Revenue Volatility (CoV)
          </h3>
          <p className="text-slate-600 mb-4" data-id="element-197">
            Measures consistency of monthly GMV. A low CoV indicates a
            predictable, stable business.
          </p>
          <FormulaBlock formula="CoV = Standard Deviation (monthly GMV) ÷ Mean (monthly GMV)" data-id="element-198" />

          <div className="overflow-x-auto mt-6" data-id="element-199">
            <table className="w-full text-sm text-left" data-id="element-200">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-201">
                <tr data-id="element-202">
                  <th className="px-4 py-3" data-id="element-203">CoV Range</th>
                  <th className="px-4 py-3" data-id="element-204">Interpretation</th>
                  <th className="px-4 py-3" data-id="element-205">Scorecard Impact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-206">
                <tr data-id="element-207">
                  <td className="px-4 py-3 font-mono text-teal-600" data-id="element-208">
                    &lt; 0.20
                  </td>
                  <td className="px-4 py-3 font-medium" data-id="element-209">Highly consistent</td>
                  <td className="px-4 py-3 text-teal-600 font-medium" data-id="element-210">
                    Maximum points
                  </td>
                </tr>
                <tr data-id="element-211">
                  <td className="px-4 py-3 font-mono text-slate-600" data-id="element-212">
                    0.20 – 0.35
                  </td>
                  <td className="px-4 py-3 font-medium" data-id="element-213">
                    Moderate variability
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-214">Partial credit</td>
                </tr>
                <tr data-id="element-215">
                  <td className="px-4 py-3 font-mono text-amber-600" data-id="element-216">
                    0.35 – 0.50
                  </td>
                  <td className="px-4 py-3 font-medium" data-id="element-217">High variability</td>
                  <td className="px-4 py-3 text-amber-600" data-id="element-218">Reduced points</td>
                </tr>
                <tr data-id="element-219">
                  <td className="px-4 py-3 font-mono text-red-600" data-id="element-220">
                    &gt; 0.50
                  </td>
                  <td className="px-4 py-3 font-medium" data-id="element-221">Unpredictable</td>
                  <td className="px-4 py-3 text-red-600 font-bold" data-id="element-222">
                    Zero points — flag
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Revenue Momentum & Customer Concentration Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-223">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-224">
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-225">
              Revenue Momentum
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-226">
              Compares last 3 months vs prior 3 months to detect growth or
              decline trajectories.
            </p>
            <FormulaBlock formula="Momentum = (Avg GMV last 3mo − Avg GMV prior 3mo) ÷ Avg GMV prior 3mo" data-id="element-227" />
            <ul className="mt-4 space-y-2 text-sm" data-id="element-228">
              <li className="flex items-center gap-2" data-id="element-229">
                <span className="w-2 h-2 rounded-full bg-teal-500" data-id="element-230"></span>{' '}
                <strong data-id="element-231">+15% or more:</strong> strong positive signal
              </li>
              <li className="flex items-center gap-2" data-id="element-232">
                <span className="w-2 h-2 rounded-full bg-slate-400" data-id="element-233"></span>{' '}
                <strong data-id="element-234">0% to +15%:</strong> stable — neutral
              </li>
              <li className="flex items-center gap-2" data-id="element-235">
                <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-236"></span>{' '}
                <strong data-id="element-237">−10% to 0%:</strong> mild decline — noted
              </li>
              <li className="flex items-center gap-2" data-id="element-238">
                <span className="w-2 h-2 rounded-full bg-red-500" data-id="element-239"></span>{' '}
                <strong data-id="element-240">−20% or worse:</strong> significant deterioration
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-241">
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-242">
              Customer Concentration
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-243">
              Measures how concentrated revenue is across the customer base
              (HHI-style index).
            </p>
            <FormulaBlock formula="Score = Σ (each customer's share of GMV)²" data-id="element-244" />
            <ul className="mt-4 space-y-2 text-sm" data-id="element-245">
              <li className="flex items-center gap-2" data-id="element-246">
                <span className="w-2 h-2 rounded-full bg-teal-500" data-id="element-247"></span>{' '}
                <strong data-id="element-248">Score near 0:</strong> highly diversified
              </li>
              <li className="flex items-center gap-2" data-id="element-249">
                <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-250"></span>{' '}
                <strong data-id="element-251">Score &gt; 0.25:</strong> moderate concentration
              </li>
              <li className="flex items-center gap-2" data-id="element-252">
                <span className="w-2 h-2 rounded-full bg-red-500" data-id="element-253"></span>{' '}
                <strong data-id="element-254">Score &gt; 0.50:</strong> high concentration — penalised
              </li>
            </ul>
          </div>
        </div>
      </div>
    </SectionWrapper>;
}